import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import * as T from "utils/types";
import * as API from 'app/public_api';

export const cntPerPage = 10;

const initList = createAsyncThunk(
    'pubTab/initList', 
    async (
        data:{
            keywordType: T.keywordType,
            searchInput: T.searchInput,
            facultyID: number,
            departmentID: number,
            year: number,
            sortType: T.pubListSortType
        }, 
        thunkAPI
    )=>{
        const {
            keywordType, searchInput, 
            facultyID, departmentID, 
            year, sortType
        } = data;
        const {currentPage}= (thunkAPI.getState() as T.RootState).pubTab;
        return await API.rPubList(
            searchInput,
            keywordType,
            currentPage+1,
            departmentID,
            facultyID,
            year,
            sortType
        );
    }
)
const loadMore = createAsyncThunk(
    'pubTab/rPubList', 
    async ( data, thunkAPI
    )=>{
        const {
            keywordType,
            searchInput,
            facultyID,
            departmentID,
            pubSpecific: { year, sortType }
        } = (thunkAPI.getState() as T.RootState).searchPgUserInputs;
        const {currentPage}= (thunkAPI.getState() as T.RootState).pubTab;
        return await API.rPubList(
            searchInput,
            keywordType,
            currentPage+1,
            departmentID,
            facultyID,
            year,
            sortType
        );
    }
)

const rPubYearStat = createAsyncThunk(
    'pubTab/rPubYearStat', 
    async (data:{
        keywordType: T.keywordType, 
        searchInput: T.searchInput,
        facultyID: number, 
        departmentID: number
    }, thunkAPI)=>{
        return await API.rPubYearStat(
            data.searchInput,
            data.keywordType,
            data.departmentID,
            data.facultyID,
        );
    }
)

export const pubTabThunks = {initList, loadMore, rPubYearStat};

export const initialState: T.PubTab = {
    total: -1,
    pubList: [],
    currentPage: 0,
    hasNext: true,
    status: 'idle',
    errorMsg: null,
    yearFilter: {
        total: -1,
        years: [],
        status: 'idle',
        errorMsg: null,
        currentRequestId: null,
    },
    currentRequestId: null,
}

// publication tab slice
export default createSlice({
    name: 'pubTab',
    initialState,
    reducers: {},
    extraReducers: builder=>{

        // initList thunk (this maybe called by other component, so it has this init steps)
        builder.addCase(initList.pending, (state, action) => {

            state.currentPage = initialState.currentPage;
            state.hasNext = initialState.hasNext;

            state.status = 'loading';
            state.errorMsg = initialState.errorMsg;
            state.currentRequestId = action.meta.requestId;

            // set the list to be cntPerPage skeleton items
            state.pubList = [
                ...new Array(cntPerPage)
            ].map(()=>({loading: true, item: null}));
        });
        builder.addCase(initList.fulfilled, (state, action) => {
            // below line makes sure the response comes from the most recent request
            if(action.meta.requestId===state.currentRequestId){
                state.status = 'succeeded';

                const {links, total_result, results} = action.payload            

                state.total = total_result;
                state.hasNext = !!links.next;
                state.currentPage += 1;

                // set the list to what we received
                state.pubList = results.map(
                    x=>({loading:false, item: x})
                );

            }
        });
        builder.addCase(initList.rejected, (state, action) => {
            // below line makes sure the response comes from the most recent request
            if(action.meta.requestId===state.currentRequestId){
                state.status = 'failed';
                state.errorMsg = action.error.message;
            }
        });

        // loadMore thunk
        builder.addCase(loadMore.pending, (state, action) => {
            state.status = 'loading';

            // add cntPerPage skeleton items to the list
            state.pubList = state.pubList.concat( 
                [...new Array(cntPerPage)].map(()=>({loading:true, item: null}))
            );
        });
        builder.addCase(loadMore.fulfilled, (state, action) => {
            state.status = 'succeeded';

            const {links, total_result, results} = action.payload            

            state.total = total_result;
            state.hasNext = !!links.next;
            state.currentPage += 1;

            // replace the last cntPerPage skeleton item list 
            // to the list we received
            state.pubList = state.pubList.slice(0, -cntPerPage).concat( 
                results.map(x=>({loading:false, item: x}))
            );
        });
        builder.addCase(loadMore.rejected, (state, action) => {
            state.status = 'failed';
            state.errorMsg = action.error.message;
        });

        // Year stat thunk
        builder.addCase(rPubYearStat.pending, (state, action) => {
            state.yearFilter.status = 'loading';
            state.yearFilter.currentRequestId = action.meta.requestId;
        });
        builder.addCase(rPubYearStat.fulfilled, (state, action) => {

            // below line makes sure the response comes from the most recent request
            if(action.meta.requestId===state.yearFilter.currentRequestId){
                state.yearFilter.status = 'succeeded';

                const {total, years} = action.payload;

                state.yearFilter.years = years;
                state.yearFilter.total = total;
            }
        });
        builder.addCase(rPubYearStat.rejected, (state, action) => {
            if(action.meta.requestId===state.yearFilter.currentRequestId){
                state.yearFilter.status = 'failed';
                state.yearFilter.errorMsg = action.error.message;
            }
        });
    }
});
